import { HintTooltip } from '@/components/HintTooltip';
import { ImageThumbnail } from '@/components/ImageThumbnail';
import { useImageViewer } from '@/components/ImageViewer';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipCode, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { equipmentTypesByIdState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { useEquipmentFieldValues } from '@/util/useEquipmentFieldValues';
import { useModelDecode } from '@/util/useModelDecode';
import { useSerialDecode } from '@/util/useSerialDecode';
import type { SerialDecode } from '@rock/serials';
import { type Equipment, EQUIPMENT_CORE_FIELD_KEYS, orderedImages } from '@rock/types';
import { unitBrandOf } from '@rock/util';
import { ImageIcon, Info } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { EquipmentActionsMenu } from './EquipmentActionsMenu';
import { EquipmentRecallsSection } from './EquipmentRecallsSection';
import { ModelDecodeInfo } from './ModelDecodeInfo';

/** Every gallery tile is this square, so a row of them reads as an even filmstrip. */
const THUMBNAIL_SIZE_PX = 192;

type EquipmentCardProps = {
    equipment: Equipment;
    /** Optional slot rendered at the top-right of the card (e.g. a home/owner link on the model page). */
    headerRight?: ReactNode;
    /** How many non-summary fields to show below the images. */
    maxFields?: number;
};

/**
 * A single equipment instance rendered for the admin portal: a properly-sized image gallery plus the
 * type, brand/model summary, and detail fields. Shared by the home equipment tab and the model page's
 * Equipment tab. The image row scrolls horizontally and sizes each image to its natural aspect at a
 * fixed height, so landscape photos read at a usable size instead of collapsing into a narrow column.
 */
export const EquipmentCard: FC<EquipmentCardProps> = ({ equipment, headerRight, maxFields = 8 }) => {
    const typesById = useAsyncAtomValue(equipmentTypesByIdState(equipment.spaceId), {});
    const type = typesById[equipment.typeId];
    const fieldValues = useEquipmentFieldValues(equipment);
    const serialDecode = useSerialDecode(equipment);
    const modelDecode = useModelDecode(equipment);

    const brandField = fieldValues.find((field) => field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.BRAND);
    const modelField = fieldValues.find((field) => field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.MODEL_NUMBER);

    const summaryText = [brandField?.displayValue, modelField?.displayValue].filter(Boolean).join(' • ');

    // When the equipment resolved to a catalog model, the model number deep-links to its model page.
    const modelId = equipment.details?.modelId;
    const modelValue =
        modelField?.displayValue && modelId ? (
            <Link to={`/products/models/${encodeURIComponent(modelId)}`} className="hover:text-primary hover:underline">
                {modelField.displayValue}
            </Link>
        ) : (
            modelField?.displayValue
        );
    const summaryContent = summaryText ? (
        <>
            {brandField?.displayValue}
            {brandField?.displayValue && modelField?.displayValue ? ' • ' : null}
            {modelValue}
        </>
    ) : null;

    // Gated on the model number being ON SCREEN, not just on the decode existing: the decode comes from
    // `details.modelId` while the summary line comes from `fields.modelNumber`, and the two part company
    // between clearing a model number and `refreshEquipmentDetails` clearing the details. Without this
    // the card would render a lone ℹ️ beside the brand, explaining a number nobody can see.
    // Rendered as a SIBLING of the truncating text (like `SerialDecodeCaption`), never inside it —
    // as the last inline child of a `truncate` container it would be the first thing clipped away,
    // silently, on exactly the long model numbers most worth decoding.
    const modelDecodeInfo = modelField?.displayValue ? <ModelDecodeInfo decode={modelDecode} /> : null;
    // Follows the brand: in the title, or on the summary line when a nickname takes the title.
    const productBrandBadge = unitBrandOf(equipment)?.source === 'product' ? <ProductBrandBadge /> : null;

    // Everything except brand/model (already in the summary line); serial, room, quantity, etc. remain.
    const detailFields = fieldValues
        .filter((field) => field !== brandField && field !== modelField)
        .slice(0, maxFields);

    const images = orderedImages(equipment);

    // Lead with the most identifying label: a nickname if set, else brand • model, else the type.
    const eyebrow = type?.title ?? 'Equipment';
    const title = equipment.nickname ?? (summaryText || type?.title) ?? 'Equipment';
    // Suppress each secondary line when it would just repeat the title.
    const showEyebrow = title !== eyebrow;
    const showSummaryLine = !!summaryText && title !== summaryText;

    const { openImage, imageViewer } = useImageViewer();

    // `group/card` is the hover scope for the image tiles: pointing anywhere at the card eases every
    // thumbnail from its crop back to the whole photo (see ImageThumbnail).
    return (
        <div className="group/card flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-0.5">
                    {showEyebrow ? (
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {eyebrow}
                        </div>
                    ) : null}
                    <div className="flex items-center gap-2">
                        <div className="truncate text-base font-medium text-foreground">
                            {equipment.nickname ?? summaryContent ?? title}
                        </div>
                        {/* Only when the title IS the brand • model line — with a nickname set, the model
                            number moves to the summary line below and the icon follows it. */}
                        {equipment.nickname ? null : modelDecodeInfo}
                        {equipment.nickname ? null : productBrandBadge}
                        {!equipment.active ? (
                            <Badge variant="outline" className="flex-shrink-0 text-muted-foreground">
                                Inactive
                            </Badge>
                        ) : null}
                    </div>
                    {showSummaryLine ? (
                        <div className="flex min-w-0 items-center gap-1">
                            <span className="truncate text-sm text-muted-foreground">{summaryContent}</span>
                            {modelDecodeInfo}
                            {productBrandBadge}
                        </div>
                    ) : null}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                    {headerRight}
                    <EquipmentActionsMenu equipment={equipment} />
                </div>
            </div>

            {images.length === 0 ? (
                <div
                    className="flex w-full items-center justify-center rounded-md bg-muted"
                    style={{ height: THUMBNAIL_SIZE_PX }}
                >
                    <ImageIcon className="h-10 w-10 text-muted-foreground" />
                </div>
            ) : (
                <div className="flex gap-2 overflow-x-auto rounded-md">
                    {images.map((image) => (
                        <ImageThumbnail
                            key={image.id}
                            image={image}
                            alt={title}
                            size={THUMBNAIL_SIZE_PX}
                            onClick={() =>
                                openImage({
                                    url: image.url,
                                    alt: title,
                                    rotation: image.rotation,
                                    width: image.width,
                                    height: image.height,
                                    highlights: image.highlights,
                                })
                            }
                        />
                    ))}
                </div>
            )}

            {detailFields.length > 0 ? (
                <dl className="grid gap-1 text-xs sm:text-sm">
                    {detailFields.map((field) => (
                        <div key={field.dataKey} className="grid grid-cols-[auto,1fr] items-start gap-x-2 gap-y-0.5">
                            <dt className="font-medium text-muted-foreground">{field.label}</dt>
                            <dd className="min-w-0 text-foreground">
                                <span className="block truncate">{field.displayValue}</span>
                                {field.dataKey === EQUIPMENT_CORE_FIELD_KEYS.SERIAL_NUMBER && serialDecode ? (
                                    <SerialDecodeCaption decode={serialDecode} />
                                ) : null}
                            </dd>
                        </div>
                    ))}
                </dl>
            ) : null}

            <EquipmentRecallsSection homeId={equipment.homeId} equipmentId={equipment.id} />

            {imageViewer}
        </div>
    );
};

/** The unit's brand is its model's, not the owner's: shown the same way the app shows it, never saved as theirs. */
const ProductBrandBadge: FC = () => (
    <HintTooltip
        className="flex-shrink-0"
        hint="The owner never gave a brand, so the unit shows its model's, as the catalog names it. It follows the model and is never saved as the owner's answer."
    >
        <Badge variant="outline" className="text-muted-foreground">
            brand from model
        </Badge>
    </HintTooltip>
);

/**
 * "Manufactured Jan 2021" under the serial value when the brand's serial format parses — the same
 * opportunistic `@rock/serials` decode the app shows on the equipment details screen. The info
 * tooltip carries the segment-by-segment breakdown and any decode caveats.
 */
const SerialDecodeCaption: FC<{ decode: SerialDecode }> = ({ decode }) => (
    <span className="flex items-center gap-1 text-xs text-muted-foreground">
        Manufactured {decode.summary}
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    aria-label="How this serial was decoded"
                    className="text-muted-foreground hover:text-foreground"
                >
                    <Info className="h-3 w-3" />
                </button>
            </TooltipTrigger>
            <TooltipContent className="text-pretty max-w-[340px] leading-snug">
                <div className="space-y-1">
                    {decode.segments.map((segment) => (
                        <div key={`${segment.label}-${segment.chars}`} className="flex gap-2">
                            <TooltipCode>{segment.chars}</TooltipCode>
                            <span>
                                {segment.label}: {segment.meaning}
                            </span>
                        </div>
                    ))}
                    {decode.notes.map((note) => (
                        <div key={note} className="opacity-80">
                            {note}
                        </div>
                    ))}
                </div>
            </TooltipContent>
        </Tooltip>
    </span>
);
